import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import { Box, Tooltip } from '@mui/material';
import { TFormType } from 'src/@types';
import { ContentBoxButtons, ContentButtonAction } from 'src/layouts/form/FormButton';
import { useStores } from 'src/models';
import {
  createBusinessSubList,
  IBusinessSubList,
} from 'src/models/management/business/BusinessSubList';
import { FormType } from 'src/static/constants';

import { SubListForm } from './SubListForm';

interface SubListManagerProps {
  formType: TFormType;
}

export const SubListManager: React.FC<SubListManagerProps> = ({ formType }) => {
  const { t } = useTranslation();
  const { setValue, watch } = useFormContext();
  const businessSubList = watch('businessSubList');
  const { alertStore } = useStores();

  // 서브 컨텐츠 추가
  const handleSubContentAdd = useCallback(() => {
    setValue('businessSubList', [...businessSubList, createBusinessSubList()], {
      shouldDirty: true, // form 수정되었음을 표시
      shouldTouch: true, // 필드가 터치되었음을 표시
      shouldValidate: true, // 설정된 값에 대해 즉시 유효성 검사 실행
    });
  }, [businessSubList, setValue]);

  // 서브 컨텐츠 삭제
  const handleSubContentDelete = (businessSubSid: number, index: number) => {
    alertStore.setOnConfirm(() => {
      if (formType === FormType.CREATE) {
        const currentList = [...businessSubList]; // 현재 리스트 복사
        currentList.splice(index, 1); // index 위치의 항목 제거

        setValue('businessSubList', currentList, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
      } else {
        const updatedFields = businessSubList.filter((item: IBusinessSubList, i: number) => {
          // 수정
          if (businessSubSid > 0) {
            return item.businessSubSid !== businessSubSid;
          }
          // 등록
          return i !== index;
        });
        setValue('businessSubList', updatedFields, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
      }
    });
    alertStore.setProps({
      open: true,
      title: '서브 컨텐츠 삭제',
      content: '해당 서브 컨텐츠를 삭제하시겠습니까? 내용은 저장되지 않습니다.',
      category: 'error',
      hasCancelButton: true,
    });
  };

  // 서브 컨텐츠가 없다면 추가버튼만 노출
  if (businessSubList.length === 0) {
    return (
      <ContentBoxButtons sx={{ mt: 2, mb: 3, justifyContent: 'flex-start' }}>
        <AddSubListButton onClick={handleSubContentAdd} />
      </ContentBoxButtons>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <ContentBoxButtons sx={{ m: 2, justifyContent: 'flex-start' }}>
        <ContentButtonAction onClick={handleSubContentAdd}>
          <AddIcon /> {t('business.form.subContentAdd')}
        </ContentButtonAction>
      </ContentBoxButtons>
      {businessSubList.map((item: IBusinessSubList, i: number) => {
        return (
          <SubListForm
            key={`${i}-${Date.now()}`} // 유니크한 key 값 생성
            index={i}
            formType={formType}
            onDelete={() =>
              handleSubContentDelete(formType === FormType.CREATE ? 0 : item.businessSubSid ?? 0, i)
            }
          />
        );
      })}
    </Box>
  );
};

// 서브 컨텐츠 추가하는 버튼
const AddSubListButton = ({ onClick }: { onClick: () => void }) => {
  const { t } = useTranslation();

  return (
    <Tooltip
      title={t('business.form.add_sub_content_tooltip')}
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            color: 'black',
            boxShadow: 1,
            fontSize: '12px',
          },
        },
      }}
    >
      <ContentButtonAction onClick={onClick}>
        <AddIcon /> {t('business.form.subContentAdd')}
      </ContentButtonAction>
    </Tooltip>
  );
};
